/**
 * GEDCOM Cross-Reference Resolver
 *
 * Resolves GEDCOM pointers (@I1@, @I2@, etc.) to actual person objects.
 * Builds an index of persons for O(1) lookups and handles unresolved references gracefully.
 *
 * Part of Phase 4 - Slice 3: FAM Record Parsing + Cross-Reference Resolution
 */

import type { ParsedFamily, ResolvedFamily } from '../types/GedcomFamily';
import type { ParsedPerson } from '../parsers/indiParser';

/**
 * Resolve family records by mapping GEDCOM pointers to actual person objects
 *
 * @param families - Array of parsed family records with raw pointers
 * @param persons - Array of parsed person records
 * @returns Array of resolved families with actual person objects
 */
export const resolveFamilies = (
  families: ParsedFamily[],
  persons: ParsedPerson[]
): ResolvedFamily[] => {
  // Build index for O(1) person lookups
  const personIndex = new Map<string, ParsedPerson>();
  persons.forEach((person) => {
    personIndex.set(person.gedcomId, person);
  });

  // Resolve each family
  return families.map((family) => resolveFamily(family, personIndex));
};

/**
 * Resolve a single family record
 *
 * @param family - Parsed family with raw pointers
 * @param personIndex - Map of GEDCOM ID to ParsedPerson
 * @returns Resolved family with actual person objects
 */
const resolveFamily = (
  family: ParsedFamily,
  personIndex: Map<string, ParsedPerson>
): ResolvedFamily => {
  const unresolvedRefs: string[] = [];

  // Resolve husband
  let husband: ParsedPerson | null = null;
  if (family.husbandId) {
    husband = personIndex.get(family.husbandId) || null;
    if (!husband) {
      // Broken reference - husband pointer exists but person not found
      unresolvedRefs.push(family.husbandId);
    }
  }

  // Resolve wife
  let wife: ParsedPerson | null = null;
  if (family.wifeId) {
    wife = personIndex.get(family.wifeId) || null;
    if (!wife) {
      // Broken reference - wife pointer exists but person not found
      unresolvedRefs.push(family.wifeId);
    }
  }

  // Resolve children
  const children: ParsedPerson[] = [];
  family.childrenIds.forEach((childId) => {
    const child = personIndex.get(childId);
    if (child) {
      children.push(child);
    } else {
      // Broken reference - child pointer exists but person not found
      unresolvedRefs.push(childId);
    }
  });

  return {
    familyId: family.familyId,
    husband,
    wife,
    children,
    unresolvedRefs,
    marriageDate: family.marriageDate,
    marriageYear: family.marriageYear,
    marriageQualifier: family.marriageQualifier,
    divorceDate: family.divorceDate,
    divorceYear: family.divorceYear,
    divorceQualifier: family.divorceQualifier,
  };
};
